/// <amd-module name="Artifacts/Root/protocols/CrossProtocolTools" />

import { Protocol } from "Artifacts/Root/Constants";
import { combineCommands } from "Artifacts/Root/protocols/Commands";
import { IProtocolService } from "Artifacts/Root/protocols/services/ProtocolService";
import { ICommand } from "Artifacts/Root/protocols/types";
import { IFeed, IPackageVersionIdentity } from "Artifacts/Root/types";
import { IVssPageContext } from "VSS/Platform/Context";
import { IListSelection } from "VSSUI/List";

type ProtocolEnum = Protocol;

/**
 * groupPackagesByProtocol creates protocol id to package identity array
 * map that groups each package tuple to its protocol.
 * @param selection object representing selected package from the package list.
 * @param pkgVersions array of package versions for determining common available commands.
 * @returns protocol id to package identity array.
 */
export function groupPackagesByProtocol(
  selection: IListSelection,
  pkgVersions: IPackageVersionIdentity[],
): Record<ProtocolEnum, IPackageVersionIdentity[]> {
  const packageMap: Record<ProtocolEnum, IPackageVersionIdentity[]> = Object.create(null);
  selection.value.forEach((s) => {
    const selectedPackageVersions = pkgVersions.slice(s.beginIndex, s.endIndex + 1);
    selectedPackageVersions.forEach((v) => {
      const protocol = v.protocol;
      if (protocol in packageMap && Array.isArray(packageMap[protocol])) {
        packageMap[protocol].push(v);
      } else {
        packageMap[protocol] = [v];
      }
    });
  });
  return packageMap;
}

/**
 * getAggregatedCommands combines batch commands for each protocol
 * into one command by intersection, filters out commands based on tag filters,
 * and return array of combined commands.
 *
 * Tag filters remove commands that do not contain ANY tag specified in tag filter set.
 *
 * @param pageContext VSS page context.
 * @param packageMap map of package and package versions grouped by protocol id.
 * @param tagFilters set of filter tags
 * @returns array of combined commands.
 */
export function getAggregatedCommands(
  pageContext: IVssPageContext,
  protocolToVersionsMap: Record<ProtocolEnum, IPackageVersionIdentity[]>,
  feed?: IFeed,
  tagFilters?: Set<string>,
): ICommand[] {
  const service: IProtocolService = pageContext.getService<IProtocolService>("IProtocolService");
  const totalCommands: Record<string, ICommand> = Object.create(null);
  let commandIdsSet: Set<string> | null = null;

  Object.keys(protocolToVersionsMap).forEach((protocolString) => {
    const protocolEnum = protocolString as ProtocolEnum;
    const protocol = service.getProtocolById(protocolEnum);
    const commands = filterCommandsByTag(
      protocol.getCommands(pageContext, protocolToVersionsMap[protocolEnum], feed),
      tagFilters,
    );

    // Create set of command IDs that are present in all selected protocols
    if (commandIdsSet == null) {
      commandIdsSet = new Set<string>(commands.map((c) => c.id));
    } else {
      commandIdsSet = new Set(
        commands.map((c) => c.id).filter((c) => (commandIdsSet as Set<string>).has(c)),
      );
    }

    commands.forEach((command) => {
      if (command.id in totalCommands) {
        totalCommands[command.id] = combineCommands([command, totalCommands[command.id]]);
      } else {
        totalCommands[command.id] = command;
      }
    });
  });
  return Object.keys(totalCommands)
    .filter((c) => commandIdsSet?.has(c))
    .map((key) => totalCommands[key]);
}

/**
 * filterCommandsByTag returns a modified array of commands for all commands that contain
 * at least 1 of the tags specified by the tagFilters. If no filter specified, commands
 * are returned unmodified
 * @param commands array of commands to filter
 * @param tagFilters set of tags to filter the commands
 * @returns all commands that contain at least 1 of the tagFilters
 */
export function filterCommandsByTag(commands: ICommand[], tagFilters?: Set<string>): ICommand[] {
  return tagFilters
    ? commands.filter((command) => [...command.tags].some((tag) => tagFilters.has(tag)))
    : commands;
}

export function filterSelectedPackages(
  selection: IListSelection,
  packages: IPackageVersionIdentity[],
): IPackageVersionIdentity[] {
  const selectedPacakges: IPackageVersionIdentity[] = [];
  selection.value.forEach((s) => {
    const selectedPackagesChunk = packages.slice(s.beginIndex, s.endIndex + 1);
    selectedPackagesChunk.forEach((pkg) => {
      selectedPacakges.push(pkg);
    });
  });
  return selectedPacakges;
}
